/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoicemailsBroadcastGroupsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Voicemail Broadcast Groups management with list view.
    Displays voicemail broadcast groups for distributing messages.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Voicemail, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import {
  navigateToVoicemailBroadcastGroupDuplicate,
} from './voicemailsBroadcastGroupsActions';
import { useVoicemailBroadcastGroupsList } from './hooks/useVoicemailBroadcastGroupsList';
import { deleteBroadcastGroup } from './api/broadcastGroupsApi';
import type { VoicemailBroadcastGroup } from './types';

export default function VoicemailsBroadcastGroupsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; group: VoicemailBroadcastGroup | null }>({
    isOpen: false,
    group: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useVoicemailBroadcastGroupsList({
    domainId: selectedDomain?.id,
  });

  const handleEdit = useCallback(
    (group: VoicemailBroadcastGroup) => {
      navigate(`/pbx/applications/voicemail-broadcast/${group.uuid || group.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (group: VoicemailBroadcastGroup) => {
      navigateToVoicemailBroadcastGroupDuplicate(navigate, group.uuid || group.id);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((group: VoicemailBroadcastGroup) => {
    setDeleteModal({ isOpen: true, group });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.group) return;

    setIsDeleting(true);
    try {
      await deleteBroadcastGroup(String(deleteModal.group.uuid || deleteModal.group.id));

      showSuccess(t('voicemailsBroadcastGroups:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, group: null });
      refresh();
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('voicemailsBroadcastGroups:messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.group, refresh, showError, showSuccess, t]);

  const handleAdd = useCallback(() => {
    navigate('/pbx/applications/voicemail-broadcast/new');
  }, [navigate]);

  const renderRowActions = useCallback(
    (item: VoicemailBroadcastGroup) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('voicemailsBroadcastGroups:actions.edit')} position="top">
          <button
            onClick={() => handleEdit(item)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('voicemailsBroadcastGroups:actions.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(item)}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('voicemailsBroadcastGroups:actions.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(item)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          title={t('voicemailsBroadcastGroups:title')}
          titleIcon={<Voicemail className="h-5 w-5 text-primary" />}
          addButtonText={t('voicemailsBroadcastGroups:actions.add')}
          renderActions={renderRowActions}
          searchPlaceholder={t('voicemailsBroadcastGroups:searchPlaceholder')}
          emptyMessage={t('voicemailsBroadcastGroups:emptyMessage')}
        />
      }
      formView={null}
    >
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, group: null })}
        onConfirm={handleDeleteConfirm}
        title={t('voicemailsBroadcastGroups:messages.deleteTitle')}
        message={t('voicemailsBroadcastGroups:messages.confirmDelete', { name: deleteModal.group?.name || '' })}
        confirmText={t('voicemailsBroadcastGroups:actions.delete')}
        cancelText={t('voicemailsBroadcastGroups:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

